/* @flow */
// typecheck: ignore file
import React from 'react';
import invariant from 'invariant';

import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import Checkbox from 'components/Checkbox.jsx';
import Copy from 'components/Copy.jsx';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog.jsx';
import Field from 'components/Field.jsx';
import Label from 'components/Label.jsx';
import Link from 'components/Link.jsx';
import OrganizationActions from 'actions/OrganizationActions';
import Row from 'components/Row.jsx';
import Textarea from 'components/Textarea.jsx';
import ToastActions from 'actions/ToastActions';

import './styles/RequestTranscriptModal.scss';

type User = {|
  id: number,
  fullName: string,
  firstName: string,
  canGetMessageHistory: boolean,
  canRequestMessageHistory: boolean,
|}

type Props = {
  users: User[],
  organizationId: number,
  organizationName: string,
  onRequestHide?: Function,
  // When true this will force to request of the message history
  // and ignore the user permissions. We use this for admins when they
  // look at the people list outside of the admin console. We still want
  // them to request message history there even if they have the permission to get them.
  forceRequestMessageHistory?: boolean,
};

type State = {
  note: string,
  inclChat: boolean,
  inclAnnouncements: boolean,
}

class RequestTranscriptModal extends React.Component {
  props: Props;

  state: State = {
    note: '',
    inclChat: true,
    inclAnnouncements: true,
  };

  componentWillMount() {
    const note = this.props.users.length === 1
      ? this.getNoteText(this.props.users[0].firstName)
      : this.getNoteText();

    this.setState({ note });
  }

  onAnnouncementCheckChange = () => {
    this.setState({ inclAnnouncements: !this.state.inclAnnouncements });
  };

  onChatCheckChange = () => {
    this.setState({ inclChat: !this.state.inclChat });
  };

  onNoteChange = (event: any) => {
    this.setState({ note: event.target.value });
  };

  getNoteText = (name?: string) => {
    return (`Hi${name ? ' ' + name : ''}, I’d like to take a quick look at how you’re using Remind. Feel free to reach out to me if you have any questions.`);
  };

  canGetAllMessageHistory = () => {
    return (
      !this.props.forceRequestMessageHistory &&
      this.props.users.every(u => u.canGetMessageHistory)
    );
  }

  getEmailLink = () => {
    const { users } = this.props;

    const email = 'schooladmin@remindhq.com';
    const subject = 'Urgent request for message history';

    const body = [
      'I’d like to request the message history of a person at my school as part of an active investigation.\n',
      `User Ids: ${users.map(u => u.id).join(', ')}\n`,
      `School: ${this.props.organizationName}`,
      `Description of investigation:\n`,
      'Please contact me as soon as possible,',
      CurrentUser.getUser().getSignature(),
    ].join('\n');

    return `mailto:${email}?subject=${subject}&body=${encodeURIComponent(body)}`;
  };

  getUsersDescription = () => {
    const { users } = this.props;

    if (users.length === 1) {
      return users[0].fullName || '1 person';
    }

    return `${this.props.users.length} people`;
  };

  invalid = () => {
    return !(this.state.inclChat || this.state.inclAnnouncements);
  };

  requestTranscript = () => {
    const { onRequestHide, organizationId, users } = this.props;
    invariant(onRequestHide, 'onRequestHide required in a modal');

    const onSuccess = () => {
      ToastActions.showSuccessToast(`Message history for ${this.getUsersDescription()} requested`);
      onRequestHide();
    };

    const onError = function() {
      ToastActions.showErrorToast('Something went wrong. Please try again.');
    };

    const transcripts = users.map(user => {
      return { user_id: user.id, request_from_affected_user: this.props.forceRequestMessageHistory || !user.canGetMessageHistory };
    });

    OrganizationActions
      .batchRequestTranscript(
        organizationId,
        transcripts,
        this.state.note,
        this.state.inclChat,
        this.state.inclAnnouncements)
      .then(onSuccess, onError);
  };

  renderBody = () => {
    const { onRequestHide } = this.props;
    const canGetAllMessageHistory = this.canGetAllMessageHistory();
    invariant(onRequestHide, 'onRequestHide required in a modal');

    return (
      <BoxBody divided>
        <Row>
          <Copy color="thunder">
            <p>
              {canGetAllMessageHistory ?
                'You’ll receive an email once you have access to the selected message history. You can also join their classes to view announcements.' :
                'This request will be sent to this person, and you’ll receive an email once you have access to the selected message history. You can also join the person’s class to view announcements.'
              }
            </p>
            <p>
              If this request is part of an active investigation, please
              {' '}
              <Link href={this.getEmailLink()}>
                contact our Support team
              </Link>
              . We’ll respond within one business day.
            </p>
          </Copy>
        </Row>
        <Field>
          <Label>What would you like to view?</Label>
          <Checkbox
            onChange={this.onAnnouncementCheckChange}
            checked={this.state.inclAnnouncements}
            label="All class announcements"
          />
          <Checkbox
            onChange={this.onChatCheckChange}
            checked={this.state.inclChat}
            label="All individual and group messages"
          />
        </Field>
        {!canGetAllMessageHistory &&
          <Field>
            <Label>Your message</Label>
            <Textarea value={this.state.note} onChange={this.onNoteChange} />
          </Field>
        }
        <Buttons axis="y">
          <Button
            className="RequestTranscriptModal-confirm"
            eventElementName="RequestTranscriptModal-confirm"
            data-test="confirm-button"
            full
            onClick={this.requestTranscript}
            disabled={this.invalid()}>
            {`${canGetAllMessageHistory ? 'Get' : 'Request'} message history`}
          </Button>
          <Button
            color="brand"
            level="text"
            onClick={onRequestHide}>
            Cancel
          </Button>
        </Buttons>
      </BoxBody>
    );
  };

  render() {
    const { onRequestHide } = this.props;
    const canGetAllMessageHistory = this.canGetAllMessageHistory();
    invariant(onRequestHide, 'onRequestHide required in a modal');

    return (
      <Dialog
        eventScreenName="RequestTranscriptModal"
        size="small"
        onRequestHide={onRequestHide}
        title={`${canGetAllMessageHistory ? 'Get' : 'Request'} message history from ${this.getUsersDescription()}`}>
        {this.renderBody()}
      </Dialog>
    );
  }
}

export default RequestTranscriptModal;



// WEBPACK FOOTER //
// ./src/organizations/components/RequestTranscriptModal.jsx